import { useState, useEffect } from 'react';
import { X, Star, Send, AlertCircle } from 'lucide-react';
import { getSurveyForm, submitSurvey } from '@/services/courseEvaluationService';
import type { SurveyFormResponse, SurveyAnswer } from '@/types/course-evaluation.types';

interface SurveyModalProps {
  moduleElementId: string;
  groupeId: string;
  academicYearId: string;
  onClose: () => void;
  onComplete: () => void;
}

export default function SurveyModal({
  moduleElementId,
  groupeId,
  academicYearId,
  onClose,
  onComplete,
}: SurveyModalProps) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<SurveyFormResponse | null>(null);
  const [answers, setAnswers] = useState<Record<string, SurveyAnswer>>({});
  const [error, setError] = useState<string | null>(null);
  const [currentSection, setCurrentSection] = useState(0);

  useEffect(() => {
    loadForm();
  }, [moduleElementId]);

  const loadForm = async () => {
    try {
      setLoading(true);
      const data = await getSurveyForm(moduleElementId);
      setForm(data);

      // Initialize answers
      const initialAnswers: Record<string, SurveyAnswer> = {};
      for (const section of data.sections) {
        for (const question of section.questions) {
          initialAnswers[question.id] = {
            questionId: question.id,
            likertScore: undefined,
            textResponse: undefined,
          };
        }
      }
      setAnswers(initialAnswers);
    } catch (err) {
      console.error('Error loading survey form:', err);
      setError('Erreur lors du chargement du sondage');
    } finally {
      setLoading(false);
    }
  };

  const handleLikertChange = (questionId: string, score: number) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], likertScore: score },
    }));
  };

  const handleTextChange = (questionId: string, text: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], textResponse: text },
    }));
  };

  const validateSection = (sectionIndex: number): boolean => {
    if (!form) return false;
    const section = form.sections[sectionIndex];
    for (const question of section.questions) {
      if (question.isRequired) {
        const answer = answers[question.id];
        if (question.questionType === 'LIKERT_5' && !answer?.likertScore) {
          return false;
        }
        if (question.questionType === 'FREE_TEXT' && !answer?.textResponse?.trim()) {
          return false;
        }
      }
    }
    return true;
  };

  const handleNext = () => {
    if (!form) return;
    if (!validateSection(currentSection)) {
      setError('Veuillez répondre à toutes les questions obligatoires');
      return;
    }
    setError(null);
    if (currentSection < form.sections.length - 1) {
      setCurrentSection((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentSection > 0) {
      setCurrentSection((prev) => prev - 1);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!form) return;
    if (!validateSection(currentSection)) {
      setError('Veuillez répondre à toutes les questions obligatoires');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Prepare answers array
      const answersArray: SurveyAnswer[] = Object.values(answers).filter(
        (a) => a.likertScore !== undefined || a.textResponse?.trim()
      );

      await submitSurvey({
        moduleElementId,
        groupeId,
        academicYearId,
        answers: answersArray,
      });

      onComplete();
    } catch (err: any) {
      console.error('Error submitting survey:', err);
      setError(err.message || 'Erreur lors de la soumission du sondage');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Chargement du sondage...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-8">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600">Impossible de charger le sondage</p>
            <button
              onClick={onClose}
              className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentSectionData = form.sections[currentSection];
  const isLastSection = currentSection === form.sections.length - 1;
  const progress = ((currentSection + 1) / form.sections.length) * 100;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Évaluation du Cours</h2>
              <p className="text-blue-100 text-sm mt-1">
                {form.moduleElementName} - {form.professorName}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          {/* Progress bar */}
          <div className="mt-4 h-2 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-2 text-sm text-blue-100">
            Section {currentSection + 1} sur {form.sections.length}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Section Title */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900">{currentSectionData.title}</h3>
            {currentSectionData.description && (
              <p className="text-sm text-gray-500 mt-1">{currentSectionData.description}</p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          {/* Questions */}
          <div className="space-y-6">
            {currentSectionData.questions.map((question, idx) => (
              <div key={question.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-start gap-2 mb-3">
                  <span className="text-sm font-medium text-gray-500 w-6">{idx + 1}.</span>
                  <p className="text-gray-800 flex-1">
                    {question.questionText}
                    {question.isRequired && <span className="text-red-500 ml-1">*</span>}
                  </p>
                </div>

                {question.questionType === 'LIKERT_5' ? (
                  <LikertScale
                    value={answers[question.id]?.likertScore}
                    onChange={(score) => handleLikertChange(question.id, score)}
                  />
                ) : (
                  <textarea
                    value={answers[question.id]?.textResponse ?? ''}
                    onChange={(e) => handleTextChange(question.id, e.target.value)}
                    placeholder="Votre réponse..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={3}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <button
            onClick={handlePrev}
            disabled={currentSection === 0}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Précédent
          </button>

          {isLastSection ? (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Envoi...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Envoyer
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Suivant
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Likert Scale Component
function LikertScale({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (score: number) => void;
}) {
  const labels = [
    { score: 1, label: 'Pas du tout d\'accord' },
    { score: 2, label: 'Pas d\'accord' },
    { score: 3, label: 'Neutre' },
    { score: 4, label: 'D\'accord' },
    { score: 5, label: 'Tout à fait d\'accord' },
  ];

  return (
    <div className="flex justify-between gap-2">
      {labels.map(({ score, label }) => (
        <button
          key={score}
          onClick={() => onChange(score)}
          className={`flex-1 flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${
            value === score
              ? 'border-blue-500 bg-blue-50 text-blue-700'
              : 'border-gray-200 hover:border-gray-300 text-gray-600'
          }`}
        >
          <div className="flex items-center gap-0.5">
            {Array.from({ length: score }).map((_, i) => (
              <Star
                key={i}
                size={14}
                className={value === score ? 'fill-blue-500 text-blue-500' : 'fill-gray-300 text-gray-300'}
              />
            ))}
          </div>
          <span className="text-xs text-center leading-tight">{label}</span>
        </button>
      ))}
    </div>
  );
}
